<template>
  <div class="mx-auto flex w-full flex-col gap-4">
    <div class="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div class="flex flex-col gap-1">
        <h1 class="text-3xl font-bold tracking-tight">
          Checklist Templates
        </h1>
        <p class="text-sm text-muted-foreground">
          Build and maintain reusable checklist templates with ordered item instructions.
        </p>
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" @click="toggleFiltersPanel">
          {{ showFiltersPanel ? 'Hide Search & Filters' : 'Show Search & Filters' }}
        </Button>
        <Button type="button" @click="openCreateModal">
          Add Checklist Template
        </Button>
      </div>
    </div>

    <p v-if="hasActiveFilters" class="text-xs text-muted-foreground">
      Filters active.
    </p>

    <form v-if="showFiltersPanel" class="flex flex-col gap-3 rounded-md border p-4 sm:flex-row sm:flex-wrap sm:items-end" @submit.prevent="handleSearch">
      <div class="flex min-w-0 flex-1 flex-col gap-2 sm:min-w-64">
        <label for="checklist-template-search-query" class="text-sm font-medium">
          Search
        </label>
        <Input
          id="checklist-template-search-query"
          v-model="searchQuery"
          placeholder="Filter by title or description"
          :disabled="isLoading"
        />
      </div>

      <div class="flex flex-col gap-2 sm:w-48">
        <label for="checklist-template-status-filter" class="text-sm font-medium">
          Status
        </label>
        <Select v-model="selectedStatus" :disabled="isLoading" @update:model-value="handleStatusChange">
          <SelectTrigger id="checklist-template-status-filter">
            <SelectValue placeholder="Select a status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">
              All Active
            </SelectItem>
            <SelectItem value="published">
              Published
            </SelectItem>
            <SelectItem value="draft">
              Draft
            </SelectItem>
            <SelectItem value="archived">
              Archived
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex flex-col gap-2 sm:w-48">
        <label for="checklist-template-sort-order" class="text-sm font-medium">
          Sort
        </label>
        <Select v-model="selectedSort" :disabled="isLoading" @update:model-value="handleSortChange">
          <SelectTrigger id="checklist-template-sort-order">
            <SelectValue placeholder="Select a sort order" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="title_asc">
              Title (A-Z)
            </SelectItem>
            <SelectItem value="created_desc">
              Newest First
            </SelectItem>
            <SelectItem value="created_asc">
              Oldest First
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div class="flex gap-2 sm:ml-auto">
        <Button type="submit" :disabled="isLoading">
          Search
        </Button>
        <Button type="button" variant="outline" :disabled="isLoading || !hasActiveFilters" @click="resetFilters">
          Reset
        </Button>
      </div>
    </form>

    <p v-if="errorMessage" class="text-sm text-destructive">
      {{ errorMessage }}
    </p>

    <p v-if="createErrorMessage" class="text-sm text-destructive">
      {{ createErrorMessage }}
    </p>

    <div v-if="isLoading && visibleChecklistTemplates.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      Loading checklist templates...
    </div>

    <p v-else-if="!isLoading && visibleChecklistTemplates.length === 0" class="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
      {{ emptyStateMessage }}
    </p>

    <div v-else class="flex flex-col gap-4">
      <template v-for="checklistTemplate in visibleChecklistTemplates" :key="checklistTemplate.id">
        <ChecklistTemplateEditor
          v-if="editingChecklistTemplateFor(checklistTemplate.id)"
          :checklist-template="editingChecklistTemplateFor(checklistTemplate.id)"
          @updated="handleChecklistTemplateEditorUpdated(checklistTemplate.id, $event)"
          @published="handleChecklistTemplateEditorPublished(checklistTemplate.id, $event)"
          @aborted="handleChecklistTemplateEditorAborted(checklistTemplate.id)"
        />
        <ChecklistTemplate
          v-else
          :checklist-template="checklistTemplate"
          show-manage-actions
          initially-minimized
          :is-deleting="deletingChecklistTemplateIds.includes(checklistTemplate.id)"
          :is-duplicating="duplicatingChecklistTemplateIds.includes(checklistTemplate.id)"
          :is-processing="startingEditChecklistTemplateIds.includes(checklistTemplate.id)"
          @edit="openChecklistTemplateEditor"
          @duplicate="duplicateChecklistTemplate"
          @delete="deleteChecklistTemplate"
        />
      </template>
    </div>

    <div v-if="showPagination" class="pt-2">
      <Pagination
        v-model:page="checklistTemplatesStore.page"
        :items-per-page="checklistTemplatesStore.perPage"
        :total="checklistTemplatesStore.total ?? 0"
        :sibling-count="1"
        show-edges
      >
        <PaginationContent v-slot="{ items: paginationItems }">
          <PaginationFirst />
          <PaginationPrevious />

          <template v-for="(paginationItem, index) in paginationItems" :key="`${paginationItem.type}-${paginationItem.value ?? index}`">
            <PaginationItem
              v-if="paginationItem.type === 'page'"
              :value="paginationItem.value"
              :is-active="paginationItem.value === checklistTemplatesStore.page"
            >
              {{ paginationItem.value }}
            </PaginationItem>
            <PaginationEllipsis v-else :index="index" />
          </template>

          <PaginationNext />
          <PaginationLast />
        </PaginationContent>
      </Pagination>
    </div>
  </div>

  <ChecklistTemplateCreateModal
    ref="createChecklistTemplateModal"
    :requires-checklist-templateable-context="false"
    @created="handleChecklistTemplateCreated"
  />
</template>

<script setup>
import { storeToRefs } from 'pinia'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { Button } from '@/components/ui/button/index.js'
import { Input } from '@/components/ui/input/index.js'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination/index.js'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select/index.js'
import ChecklistTemplate from '../components/checklist-templates/ChecklistTemplate.vue'
import ChecklistTemplateEditor from '../components/checklist-templates/ChecklistTemplateEditor.vue'
import ChecklistTemplateCreateModal from '../components/checklist-templates/ChecklistTemplateCreateModal.vue'
import { useChecklistTemplatesStore } from '../stores/checklistTemplatesStore.js'

const checklistTemplatesStore = useChecklistTemplatesStore('index-page')
const { items, isLoading, error, page, perPage, total } = storeToRefs(checklistTemplatesStore)
const createChecklistTemplateModal = ref(null)
const createErrorMessage = ref(null)
const deletingChecklistTemplateIds = ref([])
const duplicatingChecklistTemplateIds = ref([])
const editingChecklistTemplates = ref({})
const startingEditChecklistTemplateIds = ref([])
const showFiltersPanel = ref(false)
const searchQuery = ref('')
const selectedStatus = ref('active')
const selectedSort = ref('title_asc')

const visibleChecklistTemplates = computed(() => {
  return items.value.filter((checklistTemplate) => {
    if (selectedStatus.value !== 'active') {
      return true
    }

    if (checklistTemplate.status === 'archived') {
      return false
    }

    const isDraftVersion = checklistTemplate.status === 'draft'
      && Number(checklistTemplate.draft_of_id ?? 0) > 0

    return !isDraftVersion
  })
})

const hasActiveFilters = computed(() => {
  return searchQuery.value.trim().length > 0
    || selectedStatus.value !== 'active'
    || selectedSort.value !== 'title_asc'
})

const errorMessage = computed(() => {
  if (!error.value) {
    return null
  }

  return error.value.response?.data?.message ?? error.value.message ?? 'Unable to load checklist templates.'
})

const showPagination = computed(() => {
  return Number(total.value ?? 0) > Number(perPage.value ?? 0)
})

const emptyStateMessage = computed(() => {
  if (hasActiveFilters.value) {
    return 'No checklist templates matched your search or filters.'
  }

  return 'No checklist templates available yet.'
})

const resolveSortParams = () => {
  switch (selectedSort.value) {
  case 'created_desc':
    return {
      order_by: 'created_at',
      order_direction: 'desc',
    }
  case 'created_asc':
    return {
      order_by: 'created_at',
      order_direction: 'asc',
    }
  case 'title_asc':
  default:
    return {
      order_by: 'title',
      order_direction: 'asc',
    }
  }
}

const fetchChecklistTemplates = async () => {
  const sortParams = resolveSortParams()

  await checklistTemplatesStore.fetchItems({
    clear: true,
    page: checklistTemplatesStore.page,
    per_page: checklistTemplatesStore.perPage,
    ...sortParams,
    ...(selectedStatus.value !== 'active' ? { status: selectedStatus.value } : {}),
    ...(searchQuery.value.trim().length > 0 ? { search: searchQuery.value.trim() } : {}),
  })
}

const resetPaginationAndFetch = async () => {
  if (checklistTemplatesStore.page !== 1) {
    checklistTemplatesStore.page = 1
    return
  }

  await fetchChecklistTemplates()
}

const ensurePageHasItemsAfterDelete = async () => {
  if (checklistTemplatesStore.items.length > 0 || checklistTemplatesStore.page <= 1) {
    return
  }

  checklistTemplatesStore.page -= 1
  await fetchChecklistTemplates()
}

const openCreateModal = () => {
  createChecklistTemplateModal.value?.openModal()
}

const toggleFiltersPanel = () => {
  showFiltersPanel.value = !showFiltersPanel.value
}

const handleSearch = async () => {
  await resetPaginationAndFetch()
}

const handleStatusChange = async () => {
  await resetPaginationAndFetch()
}

const handleSortChange = async () => {
  await resetPaginationAndFetch()
}

const resetFilters = async () => {
  searchQuery.value = ''
  selectedStatus.value = 'active'
  selectedSort.value = 'title_asc'
  await resetPaginationAndFetch()
}

const resetIndexPageState = () => {
  showFiltersPanel.value = false
  searchQuery.value = ''
  selectedStatus.value = 'active'
  selectedSort.value = 'title_asc'
  checklistTemplatesStore.page = 1
}

const handleChecklistTemplateCreated = async (checklistTemplate) => {
  if (!checklistTemplate || Number(checklistTemplate.id) < 1) {
    return
  }

  await fetchChecklistTemplates()
  createErrorMessage.value = null
}

const editingChecklistTemplateFor = (checklistTemplateId) => {
  return editingChecklistTemplates.value[Number(checklistTemplateId)] ?? null
}

const setEditingChecklistTemplate = (checklistTemplateId, checklistTemplate) => {
  editingChecklistTemplates.value = {
    ...editingChecklistTemplates.value,
    [Number(checklistTemplateId)]: checklistTemplate,
  }
}

const clearEditingChecklistTemplate = (checklistTemplateId) => {
  const normalizedChecklistTemplateId = Number(checklistTemplateId)

  if (!Object.prototype.hasOwnProperty.call(editingChecklistTemplates.value, normalizedChecklistTemplateId)) {
    return
  }

  const nextEditingChecklistTemplates = { ...editingChecklistTemplates.value }
  delete nextEditingChecklistTemplates[normalizedChecklistTemplateId]
  editingChecklistTemplates.value = nextEditingChecklistTemplates
}

const openChecklistTemplateEditor = async (checklistTemplate) => {
  const checklistTemplateId = Number(checklistTemplate?.id ?? 0)

  if (checklistTemplateId < 1) {
    return
  }

  startingEditChecklistTemplateIds.value.push(checklistTemplateId)

  try {
    const draftChecklistTemplate = await checklistTemplatesStore.beginChecklistTemplateEditing(checklistTemplateId)

    if (!draftChecklistTemplate) {
      return
    }

    setEditingChecklistTemplate(checklistTemplateId, draftChecklistTemplate)
    createErrorMessage.value = null
  } catch (error) {
    createErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to edit checklist template.'
  } finally {
    startingEditChecklistTemplateIds.value = startingEditChecklistTemplateIds.value.filter((id) => id !== checklistTemplateId)
  }
}

const duplicateChecklistTemplate = async (checklistTemplate) => {
  const checklistTemplateId = Number(checklistTemplate?.id ?? 0)

  if (checklistTemplateId < 1) {
    return
  }

  duplicatingChecklistTemplateIds.value.push(checklistTemplateId)

  try {
    await checklistTemplatesStore.duplicateChecklistTemplate(checklistTemplateId)
    await fetchChecklistTemplates()
    createErrorMessage.value = null
  } catch (error) {
    createErrorMessage.value = error.response?.data?.message ?? error.message ?? 'Unable to duplicate checklist template.'
  } finally {
    duplicatingChecklistTemplateIds.value = duplicatingChecklistTemplateIds.value.filter((id) => id !== checklistTemplateId)
  }
}

const handleChecklistTemplateEditorUpdated = (checklistTemplateId, updatedChecklistTemplate) => {
  if (!updatedChecklistTemplate) {
    return
  }

  setEditingChecklistTemplate(checklistTemplateId, updatedChecklistTemplate)
  checklistTemplatesStore.itemPatchState(updatedChecklistTemplate)
}

const handleChecklistTemplateEditorPublished = async (checklistTemplateId, publishedChecklistTemplate) => {
  clearEditingChecklistTemplate(checklistTemplateId)

  if (publishedChecklistTemplate) {
    checklistTemplatesStore.itemPatchState(publishedChecklistTemplate)
  }

  await fetchChecklistTemplates()
}

const handleChecklistTemplateEditorAborted = async (checklistTemplateId) => {
  clearEditingChecklistTemplate(checklistTemplateId)
  await fetchChecklistTemplates()
}

const deleteChecklistTemplate = async (checklistTemplate) => {
  if (!window.confirm('Delete this checklist template?')) {
    return
  }

  deletingChecklistTemplateIds.value.push(checklistTemplate.id)

  try {
    await checklistTemplatesStore.itemDelete(checklistTemplate.id)
    await fetchChecklistTemplates()
    await ensurePageHasItemsAfterDelete()
    createErrorMessage.value = null
  } catch (deleteChecklistTemplateError) {
    createErrorMessage.value = deleteChecklistTemplateError.response?.data?.message
      ?? deleteChecklistTemplateError.message
      ?? 'Unable to delete checklist template.'
  } finally {
    deletingChecklistTemplateIds.value = deletingChecklistTemplateIds.value.filter((id) => id !== checklistTemplate.id)
  }
}

watch(page, async (currentPage, previousPage) => {
  if (currentPage === previousPage) {
    return
  }

  await fetchChecklistTemplates()
})

onMounted(async () => {
  await fetchChecklistTemplates()
})

onUnmounted(() => {
  resetIndexPageState()
})
</script>
